import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { Container } from '@mui/system';
import { Button, Skeleton } from '@mui/material';
import { ArrowBack, OpenInNew } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams, Navigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import KanbanBoard from '../components/KanbanBoard';
import PageHeader from '../components/PageHeader';
import { getTask, getBoardCards } from '../redux/ducks/task';
import { DEFAULT_KANBAN_COLUMNS, sameColumns, externalBoardLink } from '../utils/kanbanColumns';
import { useHasPermission } from '../utils/permissionUtils';
import { useIsLocked } from '../utils/lockUtils';

const ContainerStyled = styled(Container)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    marginTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
}));


const TaskBoard = () => {
    const { accountSlug, projectId, taskId } = useParams();
    const [searchParams] = useSearchParams();
    const initialCardId = searchParams.get('card');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const hasPermission = useHasPermission();
    const canSeeInternal = hasPermission('canSeeInternal');
    const canUpdateBoard = hasPermission('updateQABoard');
    const canCompleteBoardTask = hasPermission('completeQATask')
    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cards, setCards] = useState([]);
    const [columns, setColumns] = useState(null);
    const [columnsLocked, setColumnsLocked] = useState(false);
    const isLocked = useIsLocked(task?.project?.account, task?.project?.account?.client);

    const projectUrl = (accountSlug && projectId) ? `/account/${accountSlug}/project/${projectId}` : '/';

    const fetchTask = useCallback(() => {
        if (!taskId) return;
        setLoading(true);
        setTask(null);
        dispatch(getTask(taskId, (data, error) => {
            setTask(error ? null : (data || null));
            setLoading(false);
        }));
    }, [dispatch, taskId]);

    const fetchCards = useCallback(() => {
        if (!taskId) return;
        dispatch(getBoardCards(taskId, (data, error) => {
            setCards(error || !data?.cards ? [] : data.cards);
            setColumnsLocked(!error && !!data?.columnsLocked);
            const nextColumns = error || !data?.columns ? DEFAULT_KANBAN_COLUMNS : data.columns;
            setColumns((cur) => (cur && sameColumns(cur, nextColumns) ? cur : nextColumns));
        }));
    }, [dispatch, taskId]);

    useEffect(() => {
        fetchTask();
        fetchCards();
    }, [fetchTask, fetchCards]);

    const externalBoard = useMemo(() => externalBoardLink(task?.project?.externalBoard), [task?.project?.externalBoard]);

    const handleColumnsChanged = useCallback((nextColumns) => {
        if (nextColumns) setColumns(nextColumns);
    }, []);

    const handleRefresh = useCallback(() => {
        if (!taskId) return;
        dispatch(getTask(taskId, (data) => {
            if (data) setTask(data);
        }));
        fetchCards();
    }, [dispatch, taskId, fetchCards]);

    if (!canSeeInternal) {
        return <Navigate to={projectUrl} replace />;
    }

    const taskMatchesParam = task && task._id === taskId;

    if (loading || !taskMatchesParam || !columns) {
        if (!loading && !task) {
            return <Navigate to={projectUrl} replace />;
        }
        return (
            <ContainerStyled maxWidth={false}>
                <PageHeader title={<Skeleton variant="text" width={240} height={36} />} documentTitle="QA Board" subtitle="QA Board" />
                <Skeleton variant="rectangular" height={400} />
            </ContainerStyled>
        );
    }

    if (task.hasBoard === false) {
        return <Navigate to={projectUrl} replace />;
    }

    const handleBack = () => {
        navigate(projectUrl);
    };

    return (
        <ContainerStyled maxWidth={false}>
            <PageHeader
                title={task.name || "Task Board"}
                subtitle="QA Board"
                actions={
                    <>
                        {externalBoard && (
                            <Button
                                variant="contained"
                                component="a"
                                href={externalBoard.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                endIcon={<OpenInNew />}
                            >
                                Open the board on {externalBoard.host}
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            color="inherit"
                            startIcon={<ArrowBack />}
                            onClick={handleBack}
                        >
                            Back to Project
                        </Button>
                    </>
                }
            />
            <KanbanBoard
                cards={cards}
                setCards={setCards}
                taskId={task._id}
                columns={columns}
                columnsLocked={columnsLocked}
                onColumnsChanged={handleColumnsChanged}
                onRefresh={handleRefresh}
                canEdit={canUpdateBoard && !isLocked}
                canCompleteBoardTask={canCompleteBoardTask}
                isLocked={isLocked}
                initialCardId={initialCardId}
            />
        </ContainerStyled>
    );
};

export default TaskBoard;
